/**
 * Spec 083 — Admin → Page Evaluations → Detail.
 *
 * Polls `/api/v1/evaluations/:id` every 5s while the evaluation is in a
 * non-terminal state (pending / running). Terminal states (completed /
 * failed) show the full breakdown — aggregate score, per-rule results,
 * optional page-content snapshot, and a "Re-evaluate" CTA that POSTs
 * force_refresh=true and navigates to the new evaluation's detail page.
 *
 * Phase 9 Session C gap 083-1.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Icon, Spinner } from '../../components/ui/Icon.js';
import { Button } from '../../components/ui/Button.js';
import { useBrand } from '../../lib/brand-context.js';
import { useToast } from '../../hooks/useToast.js';
import { EmptyState } from '../../components/EmptyState.js';
import { ScoreBadge } from '../../components/ScoreBadge.js';
import { Breadcrumbs, type BreadcrumbItem } from '../../components/Breadcrumbs.js';
import { RoleProvisioningBanner } from '../../components/RoleProvisioningBanner.js';
import {
  createEvaluation,
  deleteEvaluation,
  getEvaluation,
  type EvaluationMode,
  type PageEvaluationDetail,
  type PageEvalStatus,
  type RuleEvalResult,
  type RuleOutcome,
  type SuggestedImprovement,
} from '../../api/pageEvaluations.js';

const EVAL_BASE_CRUMBS: BreadcrumbItem[] = [
  { label: 'Observatory', to: '/admin' },
  { label: 'Page evaluations', to: '/admin/evaluations' },
];

function truncateUrl(url: string, max = 60): string {
  if (url.length <= max) return url;
  return `${url.slice(0, max - 1)}…`;
}

const POLL_INTERVAL_MS = 5000;

const STATUS_STYLES: Record<PageEvalStatus, string> = {
  pending: 'bg-amber-100 text-amber-800',
  running: 'bg-amber-100 text-amber-800',
  completed: 'bg-emerald-100 text-emerald-800',
  failed: 'bg-rose-100 text-rose-800',
};

const OUTCOME_STYLES: Record<RuleOutcome, string> = {
  pass: 'bg-emerald-100 text-emerald-800',
  partial: 'bg-amber-100 text-amber-800',
  fail: 'bg-rose-100 text-rose-800',
  failed: 'bg-slate-200 text-slate-700',
};

const MODE_STYLES: Record<EvaluationMode, string> = {
  g_eval: 'bg-violet-100 text-violet-800',
  dag: 'bg-sky-100 text-sky-800',
};

function isTerminal(status: PageEvalStatus): boolean {
  return status === 'completed' || status === 'failed';
}

export function PageEvaluationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { selectedBrand, loading: brandsLoading } = useBrand();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [detail, setDetail] = useState<PageEvaluationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reevaluating, setReevaluating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const next = await getEvaluation(id);
      setDetail(next);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  // Poll while not terminal. Same shape as `useAdminCounts` — setInterval
  // guarded by a ref-free useEffect closure, cleaned up on unmount.
  useEffect(() => {
    if (!detail) return;
    if (isTerminal(detail.status)) return;
    const handle = setInterval(() => {
      void load();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(handle);
  }, [detail, load]);

  // Phase 9 Session E.2 (gap XC-R2-1): outer spinner short-circuit while
  // the brand list resolves — avoids the "No brand selected" flash on
  // direct-link navigation to /admin/evaluations/:id.
  if (brandsLoading) {
    return (
      <div
        data-testid="brands-loading"
        className="hub-page--reading flex flex-col items-center justify-center py-16 text-slate-500"
      >
        <Spinner className="text-violet-600" />
        <p className="mt-3 text-sm">Loading brands…</p>
      </div>
    );
  }

  if (!selectedBrand) {
    return (
      <div
        data-testid="admin-page-evaluation-detail"
        className="hub-page--reading space-y-4"
      >
        <RoleProvisioningBanner />
        <Breadcrumbs items={[...EVAL_BASE_CRUMBS, { label: 'Detail' }]} />
        <EmptyState
          title="No brand selected"
          description="Select a brand to view evaluation details."
        />
      </div>
    );
  }

  if (loading && !detail) {
    return (
      <div
        data-testid="admin-page-evaluation-detail"
        className="hub-page--reading space-y-4"
      >
        <RoleProvisioningBanner />
        <Breadcrumbs items={[...EVAL_BASE_CRUMBS, { label: 'Loading…' }]} />
        <div className="flex items-center gap-2 text-slate-500">
          <Spinner  />
          Loading evaluation…
        </div>
      </div>
    );
  }

  if (error && !detail) {
    return (
      <div
        data-testid="admin-page-evaluation-detail"
        className="hub-page--reading space-y-4"
      >
        <RoleProvisioningBanner />
        <Breadcrumbs items={[...EVAL_BASE_CRUMBS, { label: 'Error' }]} />
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          {error}
        </div>
      </div>
    );
  }

  if (!detail) return null;

  const handleReevaluate = async () => {
    setReevaluating(true);
    try {
      const res = await createEvaluation(selectedBrand.id, {
        page_url: detail.page_url,
        force_refresh: true,
      });
      addToast('Re-evaluation queued.', 'success');
      navigate(`/admin/evaluations/${res.evaluation_id}`);
    } catch (err) {
      addToast(
        err instanceof Error ? err.message : 'Re-evaluation failed',
        'error',
      );
    } finally {
      setReevaluating(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    const ok = window.confirm(
      `Permanently delete this evaluation?\n\n` +
        `This removes the evaluation and all its rule results and snapshots. ` +
        `This cannot be undone.`,
    );
    if (!ok) return;
    setDeleting(true);
    try {
      await deleteEvaluation(id);
      addToast('Evaluation deleted.', 'success');
      navigate('/admin/evaluations');
    } catch (err) {
      addToast(
        err instanceof Error ? err.message : 'Delete failed',
        'error',
      );
      setDeleting(false);
    }
  };

  const isPolling = !isTerminal(detail.status);

  return (
    <div
      data-testid="admin-page-evaluation-detail"
      className="hub-page--reading space-y-6"
    >
      <RoleProvisioningBanner />
      <Breadcrumbs
        items={[...EVAL_BASE_CRUMBS, { label: truncateUrl(detail.page_url) }]}
      />

      <header className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="hub-page__title flex items-center gap-2">
            <Icon name="bx-search-alt" className="text-violet-600" />
            Page evaluation
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <a
              href={detail.page_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 truncate font-mono text-sm text-violet-600 hover:underline"
              title={detail.page_url}
            >
              <span className="truncate">{detail.page_url}</span>
              <Icon name="bx-link-external" />
            </a>
            <StatusBadge status={detail.status} />
            {detail.content_type_detected && (
              <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs text-slate-700">
                {detail.content_type_detected}
              </span>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button variant="ghost" onClick={handleReevaluate} disabled={reevaluating} data-testid="page-evaluation-reevaluate">
            <Icon name="bx-undo" />
            {reevaluating ? 'Queuing…' : 'Re-evaluate'}
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting} data-testid="page-evaluation-delete">
            {deleting ? <Spinner /> : <Icon name="bx-trash" />}
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </div>
      </header>

      {detail.partial_completion && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Partial completion — some rules were not evaluated. See per-rule
          outcomes below for details.
        </div>
      )}

      {detail.error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          {detail.error}
        </div>
      )}

      {/* Empty-ruleset warning: a 'completed' evaluation with zero rules is
          an operational misconfig (seed rules ship as approved_inactive and
          must be activated before they participate). The detail otherwise
          looks like a working zero-score result. */}
      {detail.status === 'completed' && detail.rules_evaluated === 0 && (
        <div
          data-testid="empty-ruleset-banner"
          className="flex items-start gap-2 rounded-md border border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-700"
        >
          <Icon name="bx-info-circle" className="mt-0.5 flex-shrink-0 text-slate-500" />
          <div>
            <p className="font-semibold text-slate-900">
              No active rules at the time of this evaluation.
            </p>
            <p className="mt-1">
              The page was fetched and snapshotted, but there were no active
              rules to evaluate against — so the score is empty by design. Go
              to the{' '}
              <Link to="/admin/rules" className="font-medium text-violet-700 hover:underline">
                Rule library
              </Link>
              , activate the rules you want in play, then{' '}
              <button
                type="button"
                onClick={handleReevaluate}
                disabled={reevaluating}
                className="font-medium text-violet-700 hover:underline disabled:opacity-60"
              >
                re-evaluate this page
              </button>
              .
            </p>
          </div>
        </div>
      )}

      {/* Summary strip */}
      <section className="hub-section">
        <div className="hub-section__head">
          <h2 className="text-sm font-semibold text-slate-900">Summary</h2>
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <SummaryTile label="Aggregate score">
          <ScoreBadge
            scoreType="QualityScore"
            value={detail.aggregate_score}
            size="md"
          />
        </SummaryTile>
        <SummaryTile label="Rules">
          <div className="text-sm text-slate-700">
            <span className="font-semibold text-slate-900">
              {detail.rules_evaluated}
            </span>{' '}
            evaluated
            <div className="mt-0.5 text-xs text-slate-500">
              {detail.rules_passed} pass · {detail.rules_failed} fail ·{' '}
              {detail.rules_partial} partial
            </div>
          </div>
        </SummaryTile>
        <SummaryTile label="Cost">
          <span className="font-mono text-sm text-slate-900">
            ${detail.evaluation_cost_usd.toFixed(2)}
          </span>
        </SummaryTile>
        <SummaryTile label="Judge model">
          <span className="font-mono text-xs text-slate-700">
            {detail.judge_model_version}
          </span>
        </SummaryTile>
        <SummaryTile label="Rule-set version">
          <span
            className="font-mono text-xs text-slate-700"
            title={detail.rule_set_version}
          >
            {detail.rule_set_version.slice(0, 12)}
          </span>
        </SummaryTile>
        <SummaryTile label="Started">
          <span className="text-xs text-slate-600">
            {new Date(detail.started_at).toLocaleString()}
          </span>
        </SummaryTile>
        <SummaryTile label="Completed">
          <span className="text-xs text-slate-600">
            {detail.completed_at
              ? new Date(detail.completed_at).toLocaleString()
              : '—'}
          </span>
        </SummaryTile>
        {isPolling && (
          <SummaryTile label="Polling">
            <span className="inline-flex items-center gap-1 text-xs text-amber-700">
              <Spinner  />
              Refreshing every 5s
            </span>
          </SummaryTile>
        )}
        </div>
      </section>

      {/* Tone-of-voice guide pinned at eval time — auditability for the
          per-rule "Tone guidance applied" citations (which guide produced them). */}
      <section data-testid="tone-guide-snapshot" className="hub-section">
        <div className="hub-section__head">
          <h2 className="text-sm font-semibold text-slate-900">
            Tone-of-voice guide in effect at eval time
          </h2>
          {detail.tone_of_voice_guide_snapshot &&
            detail.tone_guide_differs_from_current && (
              <span
                data-testid="tone-guide-differs"
                className="rounded-md bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-700"
                title="The brand's current tone-of-voice guide differs from the one pinned to this evaluation."
              >
                Brand guide has changed since
              </span>
            )}
        </div>
        {detail.tone_of_voice_guide_snapshot ? (
          <details className="text-sm text-slate-700">
            <summary className="cursor-pointer text-slate-600">
              Guide pinned to this evaluation (
              {detail.tone_of_voice_guide_snapshot.length} chars) — any cited
              excerpt comes from this text
            </summary>
            <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap rounded-md bg-slate-50 p-3 font-mono text-xs text-slate-700">
              {detail.tone_of_voice_guide_snapshot}
            </pre>
          </details>
        ) : detail.tone_guide_snapshot_captured ? (
          // Snapshot was captured (we looked) and the brand had no guide — a fact.
          <p data-testid="tone-guide-none" className="text-sm text-slate-500">
            No tone-of-voice guide was set for this brand at eval time.
          </p>
        ) : detail.status === 'pending' || detail.status === 'running' ? (
          // Spec 164 — in-flight eval: the worker pins the snapshot mid-run, so a
          // null snapshot here means "not yet captured", NOT "predates capture".
          <p data-testid="tone-guide-pending" className="text-sm text-slate-500">
            Tone-of-voice guide will be captured when this evaluation runs.
          </p>
        ) : (
          // Completed/failed with no snapshot = a genuine pre-159 / backfilled row:
          // the guide was never recorded. Don't claim "none was set" — that would
          // overstate what we know (spec 162).
          <p data-testid="tone-guide-unrecorded" className="text-sm text-slate-400">
            Tone-of-voice guide not recorded for this evaluation (it predates
            tone-guide capture).
          </p>
        )}
      </section>

      {/* Rule results */}
      <section data-testid="page-evaluation-rules" className="hub-section">
        <div className="hub-section__head">
          <h2 className="text-base font-semibold text-slate-900">
            Rule results
          </h2>
          <span className="text-xs text-slate-500">
            {detail.results.length} result
            {detail.results.length === 1 ? '' : 's'}
          </span>
        </div>
        {detail.results.length === 0 ? (
          <p className="py-4 text-center text-sm text-slate-500">
            No rule results yet.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {detail.results.map((r) => (
              <RuleResultItem key={r.id} result={r} />
            ))}
          </ul>
        )}
      </section>

      {/* Snapshot (collapsed) */}
      {detail.snapshot && (
        <details
          data-testid="page-evaluation-snapshot"
          className="hub-section"
        >
          <summary className="cursor-pointer text-base font-semibold text-slate-900">
            Page snapshot
          </summary>
          <dl className="mt-3 grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
            <SnapshotRow
              label="Fetch status"
              value={String(detail.snapshot.fetch_status_code)}
            />
            <SnapshotRow
              label="JS required"
              value={detail.snapshot.js_required ? 'yes' : 'no'}
            />
            <SnapshotRow
              label="Last-Modified (header)"
              value={detail.snapshot.last_modified_header ?? '—'}
            />
            <SnapshotRow
              label="dateModified (schema)"
              value={detail.snapshot.date_modified_schema ?? '—'}
            />
            <SnapshotRow
              label="Content hash"
              value={detail.snapshot.content_hash.slice(0, 16)}
              mono
            />
            <SnapshotRow
              label="Fetched at"
              value={new Date(detail.snapshot.fetched_at).toLocaleString()}
            />
          </dl>
        </details>
      )}
    </div>
  );
}

function SummaryTile({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <dt className="text-xs font-medium text-slate-500">
        {label}
      </dt>
      <dd className="mt-1">{children}</dd>
    </div>
  );
}

function SnapshotRow({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex justify-between gap-4 border-b border-slate-100 py-1 last:border-b-0">
      <dt className="text-slate-600">{label}</dt>
      <dd className={mono ? 'font-mono text-xs text-slate-900' : 'text-slate-900'}>
        {value}
      </dd>
    </div>
  );
}

function StatusBadge({ status }: { status: PageEvalStatus }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[status]}`}
    >
      {(status === 'pending' || status === 'running') && (
        <Spinner  />
      )}
      {status}
    </span>
  );
}

function RuleResultItem({ result }: { result: RuleEvalResult }) {
  const [open, setOpen] = useState(false);
  const hasBody =
    !!result.reasoning ||
    !!result.supporting_quote ||
    !!result.suggested_improvement;

  return (
    <li className="py-2" data-testid={`rule-result-${result.rule_slug}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 rounded-md px-2 py-1 text-left hover:bg-slate-50"
        aria-expanded={open}
      >
        <span className="flex min-w-0 items-center gap-2">
          {hasBody ? (
            open ? (
              <Icon name="bx-chevron-down" className="text-slate-400" />
            ) : (
              <Icon name="bx-chevron-right" className="text-slate-400" />
            )
          ) : (
            <span className="inline-block w-[14px]" />
          )}
          <span className="truncate font-mono text-xs text-slate-900">
            {result.rule_slug}
          </span>
          <span className="text-xs text-slate-500">v{result.rule_version}</span>
        </span>
        <span className="flex shrink-0 items-center gap-2">
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-medium ${MODE_STYLES[result.evaluation_mode]}`}
          >
            {result.evaluation_mode}
          </span>
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-medium ${OUTCOME_STYLES[result.outcome]}`}
          >
            {result.outcome}
          </span>
          <span className="w-10 text-right font-mono text-sm tabular-nums text-slate-900">
            {result.score === null ? '—' : Math.round(result.score)}
          </span>
        </span>
      </button>
      {open && hasBody && (
        <div className="mt-2 space-y-2 px-7 text-sm text-slate-700">
          {result.reasoning && (
            <p className="whitespace-pre-wrap text-slate-700">
              {result.reasoning}
            </p>
          )}
          {result.supporting_quote && (
            <blockquote className="border-l-2 border-[var(--hub-color-line-strong)] px-3 py-2 text-slate-700">
              {result.supporting_quote}
            </blockquote>
          )}
          {result.suggested_improvement && (
            <SuggestedImprovementBlock improvement={result.suggested_improvement} />
          )}
          <p className="text-xs text-slate-500">
            Judge: <span className="font-mono">{result.judge_model_version}</span>
            {result.llm_tokens_used !== null && (
              <>
                {' '}
                · tokens: <span className="font-mono">{result.llm_tokens_used}</span>
              </>
            )}
            {' · '}
            evaluated{' '}
            <span className="font-mono">
              {new Date(result.evaluated_at).toLocaleString()}
            </span>
          </p>
        </div>
      )}
    </li>
  );
}

/**
 * Readable render of a rule's `suggested_improvement` payload — Before / After /
 * Rationale as labelled sections instead of a raw JSON dump.
 *
 * When the brand's tone-of-voice guide shaped the rewrite, the backend attaches
 * the verbatim guide passage as `tone_guidance_applied.excerpt`. We surface it
 * in a distinct callout so it's clear *which part of the guide is in play*. The
 * callout renders only when that excerpt is a non-empty string.
 */
function SuggestedImprovementBlock({
  improvement,
}: {
  improvement: SuggestedImprovement;
}) {
  const before = typeof improvement.before === 'string' ? improvement.before : '';
  const after = typeof improvement.after === 'string' ? improvement.after : '';
  const rationale =
    typeof improvement.rationale === 'string' ? improvement.rationale : '';

  const toneExcerptRaw = improvement.tone_guidance_applied?.excerpt;
  const toneExcerpt =
    typeof toneExcerptRaw === 'string' ? toneExcerptRaw.trim() : '';

  const hasReadableFields = !!before || !!after || !!rationale;

  return (
    <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
      {before && (
        <div>
          <p className="text-xs font-semibold text-slate-500">
            Before
          </p>
          <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">
            {before}
          </p>
        </div>
      )}
      {after && (
        <div>
          <p className="text-xs font-semibold text-slate-500">
            After
          </p>
          <p className="mt-1 whitespace-pre-wrap text-sm text-slate-900">
            {after}
          </p>
        </div>
      )}
      {rationale && (
        <div>
          <p className="text-xs font-semibold text-slate-500">
            Rationale
          </p>
          <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">
            {rationale}
          </p>
        </div>
      )}

      {toneExcerpt && (
        <div
          data-testid="tone-guidance-applied"
          className="rounded-md border border-violet-200 bg-violet-50 px-3 py-2"
        >
          <p className="text-xs font-semibold text-violet-700">
            Tone guidance applied
          </p>
          <blockquote className="mt-1 border-l-2 border-[var(--hub-color-line-strong)] pl-3 text-sm text-[var(--hub-color-ink)]">
            {toneExcerpt}
          </blockquote>
        </div>
      )}

      {/* Fallback: a row written before the structured shape existed. Show the
          raw payload rather than an empty card so nothing is silently dropped. */}
      {!hasReadableFields && !toneExcerpt && (
        <pre className="overflow-x-auto rounded-md border border-slate-200 bg-white p-2 text-xs text-slate-700">
          {JSON.stringify(improvement, null, 2)}
        </pre>
      )}
    </div>
  );
}
